// Per-project status cache. Loaded when a project opens and refreshed after
// any mutation. Mirrors the structure of store/labels.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { Status, StatusCreate, StatusUpdate } from "@juuplan/shared";
import {
  listStatuses,
  createStatus as apiCreateStatus,
  updateStatus as apiUpdateStatus,
  reorderStatuses as apiReorderStatuses,
  deleteStatus as apiDeleteStatus,
} from "../lib/api";

export interface StatusesState {
  loading: boolean;
  items: Status[];
  projectId: number | null;
  error: string | null;
}

const initial: StatusesState = {
  loading: true,
  items: [],
  projectId: null,
  error: null,
};

const statusesSignal: Signal<StatusesState> = signal(initial);
let inFlight: Promise<void> | null = null;

// Monotonic request id: stale responses must not clobber a newer project's
// statuses when the user switches projects while a load is still in flight.
let loadSeq = 0;

async function loadStatuses(projectId: number): Promise<void> {
  const seq = ++loadSeq;
  try {
    const raw = await listStatuses(projectId);
    if (seq !== loadSeq) return; // superseded
    // Sort by sortOrder so consumers always get a stable ordered list.
    const items = [...raw].sort((a, b) => a.sortOrder - b.sortOrder);
    statusesSignal.value = { loading: false, items, projectId, error: null };
  } catch (err) {
    if (seq !== loadSeq) return;
    const message = err instanceof Error ? err.message : "Unknown error";
    statusesSignal.value = {
      loading: false,
      items: [],
      projectId,
      error: message,
    };
  } finally {
    if (seq === loadSeq) inFlight = null;
  }
}

/**
 * Hook: subscribe to the statuses for the given project.
 * Triggers a load when the projectId changes, and guards against returning
 * a prior project's data while the new load is still in flight.
 */
export function useStatuses(projectId: number): StatusesState {
  useEffect(() => {
    // No `!inFlight` precondition: with it, switching projects while the first
    // load was still in flight left the hook stuck on loading:true forever
    // (the effect never re-ran once the stale load cleared the flag).
    if (statusesSignal.value.projectId !== projectId) {
      statusesSignal.value = {
        loading: true,
        items: [],
        projectId,
        error: null,
      };
      inFlight = loadStatuses(projectId);
    }
  }, [projectId]);

  const state = statusesSignal.value;
  if (state.projectId !== projectId) {
    return { loading: true, items: [], projectId, error: null };
  }
  return state;
}

/** Force a fresh fetch — use after any schema-changing mutation. */
export function refreshStatuses(projectId: number): Promise<void> {
  statusesSignal.value = { ...statusesSignal.value, loading: true };
  inFlight = loadStatuses(projectId);
  return inFlight;
}

/** Imperatively load statuses for a project (no React hook needed). */
export function ensureStatuses(projectId: number): Promise<void> {
  if (statusesSignal.value.projectId === projectId && !statusesSignal.value.loading) {
    return Promise.resolve();
  }
  if (inFlight && statusesSignal.value.projectId === projectId) {
    return inFlight;
  }
  statusesSignal.value = { loading: true, items: [], projectId, error: null };
  inFlight = loadStatuses(projectId);
  return inFlight;
}

// ---------- CRUD helpers ----------

/** Create a new status and refresh the cache. */
export async function addStatus(
  projectId: number,
  body: StatusCreate
): Promise<Status> {
  const created = await apiCreateStatus(projectId, body);
  await refreshStatuses(projectId);
  return created;
}

/** Update a status and refresh the cache. */
export async function patchStatus(
  id: number,
  projectId: number,
  body: StatusUpdate
): Promise<Status> {
  const updated = await apiUpdateStatus(id, body);
  await refreshStatuses(projectId);
  return updated;
}

/** Reorder statuses and update the cache optimistically. */
export async function reorderStatuses(
  projectId: number,
  orderedIds: number[]
): Promise<void> {
  const cur = statusesSignal.value;
  if (cur.projectId === projectId) {
    // Optimistic: reorder locally before the server round-trip.
    const byId = new Map(cur.items.map((s) => [s.id, s]));
    const reordered = orderedIds
      .map((id) => byId.get(id))
      .filter((s): s is Status => s !== undefined);
    statusesSignal.value = { ...cur, items: reordered };
  }
  try {
    await apiReorderStatuses(projectId, orderedIds);
  } catch {
    // Revert on error by re-fetching the authoritative order.
    await refreshStatuses(projectId);
  }
}

/** Delete a status and remove it from the cache. */
export async function removeStatus(
  id: number,
  projectId: number
): Promise<void> {
  await apiDeleteStatus(id);
  const cur = statusesSignal.value;
  if (cur.projectId === projectId) {
    statusesSignal.value = {
      ...cur,
      items: cur.items.filter((s) => s.id !== id),
    };
  }
}
